/**
 * Telemetry scrubbing for sensitive values.
 *
 * The Honeybadger SDK's `filters` option only sanitizes `notice.url`,
 * `params`, and `session` at payload-build time. Two channels bypass it
 * entirely and must be scrubbed here instead:
 *
 *  - breadcrumb metadata (navigation `from`/`to`, request URLs) — attached to
 *    the notice after `beforeNotify` runs, so it can only be scrubbed when the
 *    breadcrumb is recorded;
 *  - `notice.context` — sent verbatim.
 *
 * @memberof @albert-io/notifier
 */

/**
 * Key fragments treated as sensitive, matched case-insensitively as
 * substrings of query-parameter and context keys (same semantics as the
 * Honeybadger SDK's own `filters` matching). Deliberately broad stems —
 * `verification` covers both `verification_code` and `verificationCode`,
 * `consent` covers `consent_id` and `consent_email` — because over-redacting
 * fails closed.
 */
export const CONTENT_FILTERS = [
  'token',
  'email',
  'username',
  'password',
  'stripe_customer_id',
  'first_name',
  'last_name',
  'salutation',
  'consent',
  'verification'
];

const FILTERED = '[FILTERED]';

const SENSITIVE_QUERY_PARAM_RE = new RegExp(
  `([?&][^?&#=]*(?:${CONTENT_FILTERS.join('|')})[^?&#=]*)=[^&#]*`,
  'gi'
);

const isSensitiveKey = (key) => {
  const lowered = key.toLowerCase();
  return CONTENT_FILTERS.some((filter) => lowered.includes(filter));
};

/**
 * Replace the value of any sensitive query parameter in a URL (or any string
 * containing a query string) with [FILTERED]. Non-strings pass through.
 */
export const filterUrl = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(SENSITIVE_QUERY_PARAM_RE, `$1=${FILTERED}`);
};

/**
 * Scrub sensitive query parameters out of a breadcrumb's string metadata
 * values (navigation breadcrumbs carry full `from`/`to` URLs, request
 * breadcrumbs carry `url`).
 */
export const scrubBreadcrumbOpts = (opts) => {
  if (!opts || typeof opts !== 'object' || !opts.metadata || typeof opts.metadata !== 'object') {
    return opts;
  }
  const metadata = {};
  for (const [key, value] of Object.entries(opts.metadata)) {
    metadata[key] = filterUrl(value);
  }
  return {...opts, metadata};
};

const MAX_SCRUB_DEPTH = 8;

const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

const scrubValue = (value, depth) => {
  if (depth >= MAX_SCRUB_DEPTH) return value;
  if (Array.isArray(value)) {
    return value.map((entry) => scrubValue(entry, depth + 1));
  }
  if (isPlainObject(value)) {
    const scrubbed = {};
    for (const [key, entry] of Object.entries(value)) {
      scrubbed[key] = isSensitiveKey(key) ? FILTERED : scrubValue(entry, depth + 1);
    }
    return scrubbed;
  }
  return filterUrl(value);
};

/**
 * Recursively redact context values whose key matches a sensitive fragment,
 * and scrub sensitive query params from any string values. Non-plain objects
 * (class instances, Immutable collections) are left untouched rather than
 * traversed.
 */
export const scrubContext = (context) => {
  if (!isPlainObject(context)) return context;
  return scrubValue(context, 0);
};
